// The fetch half of upstream's `FileDownload.saveFile`
// (`common/download/file.download.service.ng1.ts`), shared by every endpoint that answers
// with a file rather than JSON. `packages/ui`'s `saveFile` is the write half; splitting them
// is what keeps the request shape testable without a DOM.
import { apiUrl } from './request.ts'

export interface DownloadedFile {
  blob: Blob
  filename: string
}

export interface FetchFileOptions {
  method?: 'GET' | 'POST'
  /** Serialised as JSON with the matching content type, exactly as upstream's `data` is. */
  body?: unknown
  /** Used when the server composed no usable `Content-Disposition` — see below. */
  fallbackFilename: string
  /** What the error says when the server answers an error instead of a file. */
  failureMessage?: string
}

/**
 * ⚠️ A 200 CARRYING JSON IS AN ERROR, NOT A FILE. `RestUtils.createErrorResponse` answers 200
 * with `{ error: { message } }`, so a status check alone saves the failure to disk as a file
 * the user then opens to find a stack trace. Upstream decodes and rethrows; so does this.
 * Checked before `response.ok` because that is the case `ok` cannot see.
 */
export async function fetchFile(path: string, options: FetchFileOptions): Promise<DownloadedFile> {
  const { method = 'GET', body, fallbackFilename, failureMessage = 'The download failed.' } = options
  const response = await fetch(apiUrl(path), {
    method,
    credentials: 'include',
    ...(body === undefined ? {} : { headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) }),
  })

  const contentType = response.headers.get('content-type') ?? ''
  const blob = await response.blob()

  if (contentType.startsWith('application/json')) {
    const parsed: unknown = JSON.parse(await blob.text())
    const message =
      typeof parsed === 'object' && parsed !== null && 'error' in parsed
        ? (parsed as { error?: { message?: string } }).error?.message
        : undefined
    throw new Error(message ?? failureMessage)
  }

  if (!response.ok) throw new Error(`${failureMessage.replace(/\.$/, '')} with ${String(response.status)}.`)

  return { blob, filename: filenameFromDisposition(response.headers.get('content-disposition'), fallbackFilename) }
}

/**
 * Upstream strips the `attachment; filename=` prefix and the quotes by hand rather than
 * parsing the header, and keeps whatever is left. Reproduced, including the fallback: a name
 * containing `?` is the server having failed to compose one.
 */
export function filenameFromDisposition(disposition: string | null, fallback: string): string {
  if (disposition === null || !disposition.includes('filename')) return fallback
  const name = disposition.replace('attachment; filename=', '').replace(/"/g, '').trim()
  return name.length === 0 || name.includes('?') ? fallback : name
}
